import * as React from 'react';
import { Translate } from '../../../../shared/LanguageService';
import { FormGroup, FormControl, ControlLabel, Checkbox, Row, Col } from 'react-bootstrap';
import { ICreateOrderAddress, INamedIdentity } from '../../Entities';
import FormSelect from '../../../../components/common/FormSelect';
import ValidatableFormGroup from '../../../../components/common/ValidatableFormGroup';
import AddressMapButton from '../../../../components/common/addressValidation/AddressMapButton';
import ICoordinate from '../../../map/ICoordinate';
import GooglePlacesAutocomplete from '../../../../components/common/GooglePlacesAutocomplete';

interface IProps {
    address: ICreateOrderAddress;
    availablePrivateObjectTypes: INamedIdentity[];
    availableCommercialObjectTypes: INamedIdentity[];
    onDataChange: (e: any) => void;
    isFormValid: () => boolean;
    errors: any;
    isPackageOrder: boolean;
    isCommercial: boolean;
    objectTypeLockDown: any;
    onCustomChange: (name: string, value: any) => void;
    onPointChange: (position: ICoordinate) => void;
}

const CreateOrderAddressForm = (props: IProps) => {

    const handleOptionSelect = async (placeDetails: any) => {
        if (placeDetails) {
            try {
                const addressComponents = placeDetails.address_components;

                const getAddressComponent = (type: string) => {
                    const component = addressComponents.find((component: any) =>
                        component.types.includes(type)
                    );
                    return component ? component.long_name : '';
                };

                const addressLine1 = `${getAddressComponent('route')} ${getAddressComponent('street_number')} `;
                const addressCity = getAddressComponent('locality') || getAddressComponent('postal_town');
                const addressPostalNumber = getAddressComponent('postal_code');
                const addressDistrict = getAddressComponent('sublocality_level_1');

                props.onCustomChange('address.addressLine1', addressLine1);
                props.onCustomChange('address.addressCity', addressCity);
                props.onCustomChange('address.addressPostalNumber', addressPostalNumber);
                if (addressDistrict) {
                    props.onCustomChange('address.addressDistrict', addressDistrict);
                }
            } catch (error) {
                console.error('Error fetching place details:', error);
            }            
        }
    };

    return (
        <fieldset>
            <legend>{Translate('orders/create/form/header/address')}</legend>
            <form>
                <FormGroup>
                    <GooglePlacesAutocomplete onSelect={handleOptionSelect} />
                </FormGroup>                    
                <ValidatableFormGroup
                    required
                    validationKey="address.AddressLine1"
                    errors={props.errors}
                >
                    <ControlLabel>
                        {Translate('orders/create/form/input-label/address-line-1')}
                    </ControlLabel>
                    <FormControl
                        type="text"
                        name="address.addressLine1"
                        value={props.address.addressLine1 ? props.address.addressLine1 : ''}
                        onChange={props.onDataChange}
                    />
                </ValidatableFormGroup>
                <Row>
                    {props.isPackageOrder && (
                        <Col xs={5} sm={5}>
                            <ValidatableFormGroup
                                required
                                validationKey="address.AddressPostalNumber"
                                errors={props.errors}
                            >
                                <ControlLabel>
                                    {Translate(
                                        'orders/create/form/input-label/address-postal-number'
                                    )}
                                </ControlLabel>
                                <FormControl
                                    type="text"
                                    name="address.addressPostalNumber"
                                    value={
                                        props.address.addressPostalNumber
                                            ? props.address.addressPostalNumber
                                            : ''
                                    }
                                    onChange={props.onDataChange}
                                />
                            </ValidatableFormGroup>
                        </Col>
                    )}
                    <Col xs={props.isPackageOrder ? 5 : 12} sm={props.isPackageOrder ? 6 : 12}>
                        <ValidatableFormGroup
                            required
                            validationKey="address.AddressCity"
                            errors={props.errors}
                        >
                            <ControlLabel>
                                {Translate('orders/create/form/input-label/address-city')}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="address.addressCity"
                                value={props.address.addressCity ? props.address.addressCity : ''}
                                onChange={props.onDataChange}
                            />
                        </ValidatableFormGroup>
                    </Col>
                    {props.isPackageOrder && (
                        <AddressMapButton
                            addressLine={props.address.addressLine1}
                            onAddressLineChange={(addressLine1: string) =>
                                props.onCustomChange('address.addressLine1', addressLine1)
                            }
                            postalNumber={props.address.addressPostalNumber}
                            onPostalNumberChange={(postalNumber: string) =>
                                props.onCustomChange('address.addressPostalNumber', postalNumber)
                            }
                            city={props.address.addressCity}
                            onCityChange={(city: string) =>
                                props.onCustomChange('address.addressCity', city)
                            }
                            point={props.address.addressPointLocation}
                            onPointChange={props.onPointChange}
                            error={props.errors['address.AddressPointLocation']}
                        />
                    )}
                </Row>

                {props.isPackageOrder && !props.isCommercial && (
                    <ValidatableFormGroup
                        validationKey="address.AddressDistrict"
                        errors={props.errors}
                    >
                        <ControlLabel>
                            {Translate('orders/create/form/input-label/address-district')}
                        </ControlLabel>
                        <FormControl
                            type="text"
                            name="address.addressDistrict"
                            value={
                                props.address.addressDistrict ? props.address.addressDistrict : ''
                            }
                            onChange={props.onDataChange}
                        />
                    </ValidatableFormGroup>
                )}
                <Row>
                    {props.isPackageOrder && !props.isCommercial && (
                        <Col md={4}>
                            <ValidatableFormGroup
                                validationKey="address.ObjectAreaSquareMeters"
                                errors={props.errors}
                            >
                                <ControlLabel>
                                    {Translate(
                                        'orders/create/form/input-label/object-area-square-meters'
                                    )}
                                </ControlLabel>
                                <FormControl
                                    type="text"
                                    name="address.objectAreaSquareMeters"
                                    value={
                                        props.address.objectAreaSquareMeters
                                            ? props.address.objectAreaSquareMeters.toString()
                                            : ''
                                    }
                                    onChange={props.onDataChange}
                                />
                            </ValidatableFormGroup>
                        </Col>
                    )}
                    {props.isPackageOrder && !props.isCommercial && (
                        <Col md={4}>
                            <ValidatableFormGroup
                                validationKey="address.ObjectNumberOfRooms"
                                errors={props.errors}
                            >
                                <ControlLabel>
                                    {Translate(
                                        'orders/create/form/input-label/object-number-of-rooms'
                                    )}
                                </ControlLabel>
                                <FormControl
                                    type="number"
                                    name="address.objectNumberOfRooms"
                                    value={
                                        props.address.objectNumberOfRooms
                                            ? props.address.objectNumberOfRooms.toString()
                                            : ''
                                    }
                                    onChange={props.onDataChange}
                                />
                            </ValidatableFormGroup>
                        </Col>
                    )}
                    {props.isPackageOrder && !props.isCommercial && (
                        <Col md={4}>
                            <ValidatableFormGroup
                                validationKey="address.objectYearOfConstruction"
                                errors={props.errors}
                            >
                                <ControlLabel>
                                    {Translate(
                                        'orders/create/form/input-label/object-year-of-construction'
                                    )}
                                </ControlLabel>
                                <FormControl
                                    type="text"
                                    name="address.objectYearOfConstruction"
                                    value={
                                        props.address.objectYearOfConstruction
                                            ? props.address.objectYearOfConstruction.toString()
                                            : ''
                                    }
                                    onChange={props.onDataChange}
                                />
                            </ValidatableFormGroup>
                        </Col>
                    )}
                </Row>
                <Row>
                    <Col md={6}>
                        {props.isPackageOrder && (
                            <ValidatableFormGroup
                                required
                                validationKey="address.ObjectType"
                                errors={props.errors}
                            >
                                <ControlLabel>
                                    {Translate('orders/create/form/input-label/object-type')}
                                </ControlLabel>
                                <FormSelect
                                    componentClass="select"
                                    name="address.objectType"
                                    value={
                                        props.address.objectType
                                            ? props.address.objectType.toString()
                                            : ''
                                    }
                                    onChange={props.onDataChange}
                                >
                                    {
                                        <option key="-1" value="-1">
                                            {Translate(
                                                'orders/create/form/select-object-type-placeholder'
                                            )}
                                        </option>
                                    }
                                    {!props.isCommercial &&
                                        props.availablePrivateObjectTypes
                                            .filter((x: any) => {
                                                return (
                                                    !props.objectTypeLockDown ||
                                                    x.id === props.objectTypeLockDown.value
                                                );
                                            })
                                            .map((x: any) => (
                                                <option value={x.id} key={x.id}>
                                                    {Translate(x.name)}
                                                </option>
                                            ))}
                                    {props.isCommercial &&
                                        props.availableCommercialObjectTypes
                                            .filter((x: any) => {
                                                return (
                                                    !props.objectTypeLockDown ||
                                                    x.id === props.objectTypeLockDown.value
                                                );
                                            })
                                            .map((x: any) => (
                                                <option value={x.id} key={x.id}>
                                                    {Translate(x.name)}
                                                </option>
                                            ))}
                                </FormSelect>
                            </ValidatableFormGroup>
                        )}
                    </Col>
                </Row>
                {props.isPackageOrder &&
                    !props.isCommercial &&
                    props.address.objectType === 1 /* apartment */ && (
                        <Row>
                            <Col md={4}>
                                <ValidatableFormGroup
                                    validationKey="address.ApartmentAccessCode"
                                    errors={props.errors}
                                >
                                    <ControlLabel>
                                        {Translate(
                                            'orders/create/form/input-label/object-type/apartment/access-code'
                                        )}
                                    </ControlLabel>
                                    <FormControl
                                        type="text"
                                        name="address.apartmentAccessCode"
                                        value={
                                            props.address.apartmentAccessCode
                                                ? props.address.apartmentAccessCode
                                                : ''
                                        }
                                        onChange={props.onDataChange}
                                    />
                                </ValidatableFormGroup>
                            </Col>
                            <Col md={4}>
                                <ValidatableFormGroup
                                    validationKey="address.ApartmentFloor"
                                    errors={props.errors}
                                >
                                    <ControlLabel>
                                        {Translate(
                                            'orders/create/form/input-label/object-type/apartment/floor'
                                        )}
                                    </ControlLabel>
                                    <FormControl
                                        type="text"
                                        name="address.apartmentFloor"
                                        value={
                                            props.address.apartmentFloor
                                                ? props.address.apartmentFloor
                                                : ''
                                        }
                                        onChange={props.onDataChange}
                                    />
                                </ValidatableFormGroup>
                            </Col>
                            <Col md={4} className="no-label">
                                <FormGroup>
                                    <Checkbox
                                        className="inline"
                                        name="address.apartmentHasBalcony"
                                        checked={props.address.apartmentHasBalcony}
                                        onChange={props.onDataChange}
                                    >
                                        <span>
                                            {Translate(
                                                'orders/create/form/input-label/object-type/apartment/has-balcony'
                                            )}
                                        </span>
                                    </Checkbox>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
            </form>
        </fieldset>
    );
};

export default CreateOrderAddressForm;
